import { Route, Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GetTicketNumberSequenceQuery as GetTicketNumberSequence } from '__generated__/graphql';
import { encodeUrl } from '../../../common/helpers';
import EditManualTicketNumbersForm from './EditManualTicketNumbersForm';
import EditTicketNumberSequenceCriteriaForm from './EditTicketNumberSequenceCriteriaForm';
import EditTicketNumberSequenceInfoForm from './EditTicketNumberSequenceInfoForm';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';

type TicketNumberSequence = GetTicketNumberSequence['project']['ticket_number_sequence'];

export interface EditTicketNumberSequenceFormProps {
  sequence: TicketNumberSequence;
}

const EditTicketNumberSequenceForm = ({ sequence }: EditTicketNumberSequenceFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
      <UI.Nav>
        <UI.NavLink
          to={`/${project.id}/participants/bib-numbers/${sequence.id}?${encodeUrl({ event: sequence.event.id })}`}
          exact
          resetScrollPosition={false}
        >
          {t('basic_info')}
        </UI.NavLink>
        {!sequence.scanner && (
          <UI.NavLink
            to={`/${project.id}/participants/bib-numbers/${sequence.id}/criteria?${encodeUrl({ event: sequence.event.id })}`}
            resetScrollPosition={false}
          >
            {t('criteria')}
          </UI.NavLink>
        )}
        <UI.NavLink
          to={`/${project.id}/participants/bib-numbers/${sequence.id}/manual-entry?${encodeUrl({ event: sequence.event.id })}`}
          resetScrollPosition={false}
        >
          {t('manual_entry')}
          {sequence.locked_ticket_number_count > 0 && (
            <UI.Small sc={{ muted: true }} style={{ fontWeight: 400 }}>
              {` (${formatNumber(sequence.locked_ticket_number_count)})`}
            </UI.Small>
          )}
        </UI.NavLink>
      </UI.Nav>

      <UI.Div>
        <Switch>
          <Route path="/:projectId/participants/bib-numbers/:ticketNumberSequenceId/criteria">
            <EditTicketNumberSequenceCriteriaForm sequence={sequence} />
          </Route>
          <Route
            path="/:projectId/participants/bib-numbers/:ticketNumberSequenceId/manual-entry"
          >
            <EditManualTicketNumbersForm sequence={sequence} />
          </Route>
          <Route path="/:projectId/participants/bib-numbers/:ticketNumberSequenceId">
            <EditTicketNumberSequenceInfoForm sequence={sequence} />
          </Route>
        </Switch>
      </UI.Div>
    </UI.FormGrid>
  );
};

export default EditTicketNumberSequenceForm;
